import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { createRoot } from 'react-dom/client';

import { rawHTMLLoader } from 'app/component/rawHTMLLoader';
import { exceptionLogger } from 'app/logic/pages/error/exceptionLogger';
import { DownloadErrorModal } from 'app/component/download-error-modal/DownloadErrorModal';
import { useIsDesktop } from 'app/utils/useWindowType';
import { PDFViewer } from '@alfa-retail-react/components';

/**
 * Режим открытия ресурса. Указывает каким обьразом ресурс будет пердставлен пользователю.
 */
export enum OpenResourceMode {
    /**
     * Скчачивание ресурса
     */
    DOWNLOAD = 'DOWNLOAD',

    /**
     * Открытие ресурса в новой вкладке.
     */
    NEW_TAB = 'NEW_TAB',
}

export interface DownloadResourceLinkProps {
    className?: string;

    download: () => Promise<{ file: Blob; filename: string }>;

    trigger: React.ReactElement;

    openResourceMode: OpenResourceMode;

    /**
     * Титул страницы открытого документа
     */
    title?: string;
}

export const DownloadResourceLink = observer(
    (props: DownloadResourceLinkProps) => {
        const downloadLink = useRef<HTMLAnchorElement>();
        const [isModalOpen, setIsModalOpen] = useState(false);
        const handleCloseModal = () => setIsModalOpen(false);
        const [isDesktop] = useIsDesktop();
        const [isDownloading, setIsDownloading] = useState(false);
        const [fileName, setFileName] = useState('');
        const [file, setFile] = useState<Blob>();
        const [isOpen, setIsOpen] = useState(false);
        const isDownloadMode =
            props.openResourceMode === OpenResourceMode.DOWNLOAD;

        const onClose = () => setIsOpen(false);

        const viewFile = (file: Blob, filename: string) => {
            setFile(file);

            const clearFileName = filename?.replace('.pdf', '');
            setFileName(clearFileName);

            if (!isDownloadMode) {
                setIsOpen(true);
            }
        };

        useEffect(() => {
            if (file && fileName && isDownloadMode) {
                handleDownloadFile();
            }
        }, [file]);

        const handleViewFile = async () => {
            if (isDownloading) {
                return;
            }

            setIsDownloading(true);

            props
                .download()
                .then((response) => {
                    setIsDownloading(false);

                    if (!response) {
                        return;
                    }

                    const { file, filename } = response;
                    viewFile(file, filename);
                    handleCloseModal();
                })
                .catch(
                    exceptionLogger('DownloadResourceLink', 'props.download'),
                )
                .catch(() => {
                    setIsDownloading(false);
                    setIsModalOpen(true);
                });
        };

        const handleOpenFile = () => {
            /**
             * Обход блокировки всплывающих окон https://projects.alfabank.by/browse/CC2C-1014
             * Окно не блокируется при открытии его непосредственно событием от пользователя
             * Поэтому сначала открывается окно с лодером, по завершению загрузки файла URL в нем меняется на файл
             */
            const win: Window = window.open('', '_blank');
            win.document.title = props.title;

            const rootEl = win.document.createElement('div');
            win.document.body.append(rootEl);
            const root = createRoot(rootEl);

            win.document.head.insertAdjacentHTML(
                'beforeend',
                rawHTMLLoader.style,
            );

            const element = rawHTMLLoader.element;
            root.render(element);

            // workaround for IE
            if (
                window.navigator &&
                (window.navigator as any).msSaveOrOpenBlob
            ) {
                (window.navigator as any).msSaveOrOpenBlob(file, fileName);
            } else {
                const fileAsURL = URL.createObjectURL(file);

                if (win.document.location) {
                    win.document.location.href = fileAsURL;
                }

                URL.revokeObjectURL(fileAsURL);
            }
        };

        const handleDownloadFile = () => {
            // workaround for IE
            if (
                window.navigator &&
                (window.navigator as any).msSaveOrOpenBlob
            ) {
                (window.navigator as any).msSaveOrOpenBlob(file, fileName);
            } else {
                const fileAsURL = URL.createObjectURL(file);
                const downloadElement =
                    !!downloadLink.current && !!downloadLink.current.click
                        ? downloadLink.current
                        : document.createElement('a');

                downloadElement.href = fileAsURL;
                downloadElement.download = fileName;
                downloadElement.click();

                URL.revokeObjectURL(fileAsURL);
            }
        };

        return (
            <>
                <DownloadErrorModal
                    isDesktop={isDesktop}
                    isDownloading={isDownloading}
                    open={isModalOpen}
                    onClose={handleCloseModal}
                    onDownload={handleViewFile}
                />
                <a ref={downloadLink} />
                <div className={props.className} onClick={handleViewFile}>
                    {props.trigger}
                </div>
                <PDFViewer
                    isDesktop={isDesktop}
                    isOpen={isOpen}
                    file={file}
                    fileName={props.title || fileName}
                    onClose={onClose}
                    onOpenFile={handleOpenFile}
                    onDownloadFile={handleDownloadFile}
                />
            </>
        );
    },
);
